import {
  ClipboardDropdownItem,
  Dropdown,
  DropdownDivider,
  DropdownItem,
} from 'ui/components/Dropdown'
import {
  PencilIcon,
  MailIcon,
  XIcon,
  CashIcon,
  ArchiveIcon,
  RefreshIcon,
  UsersIcon,
  ExternalLinkIcon,
  BookOpenIcon,
} from '@heroicons/react/solid'
import { useHistory } from 'react-router-dom'
import { useActiveFeatureFlags } from 'contexts/SiteConfig'
import { useCurrentUserCanForCurrentBrand } from 'hooks/UseCurrentUserCan'
import { CUSTOMERS_MANAGE } from 'constants/permissions'
import { useTranslation } from 'react-i18next'
import { useIsInternalUser } from 'components/InternalOnlyBox'
import { Membership } from '@trybeapp/sdk'
import { FC } from 'react'

interface MembershipDropdownProps {
  membership: Membership
  onEdit: (id: Membership['id']) => void
  onRequestMandate: (id: Membership['id']) => void
  onAddPayment: (id: Membership['id']) => void
  onCancel: (id: Membership['id']) => void
  onRetryPayment: (id: Membership['id']) => void
  onArchive: (id: Membership['id']) => void
  onManageMembers: (id: Membership['id']) => void
  isLead?: boolean
  canRecordManualPayment?: boolean
  onOpenAuditLog: () => void
}

export const MembershipDropdown: FC<MembershipDropdownProps> = ({
  membership,
  onEdit,
  onRequestMandate,
  onAddPayment,
  onCancel,
  onRetryPayment,
  onArchive,
  onManageMembers,
  isLead,
  canRecordManualPayment,
  onOpenAuditLog,
}) => {
  const { t } = useTranslation()
  const featureFlags = useActiveFeatureFlags()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanManageClients = can(CUSTOMERS_MANAGE)
  const isInternalUser = useIsInternalUser()

  /**
   * We can request a new mandate if we don't have one, its revoked,
   * or the setup is unpaid and we dont have one.
   */
  const canRequestMandate =
    isLead &&
    ['needs_attention', 'active'].includes(membership.status) &&
    !membership.type.offline_payments

  const leadMember = membership.members.find((m) => m.is_lead)

  // Retry payment for the membership if it's in a 'payment_failed' state.
  // For sites using revenue schedules, it's the charge that needs to be retried
  // rather than the membership
  const canRetryPayment = Boolean(
    membership.status === 'needs_attention' &&
      membership.attention_reason === 'payment_failed' &&
      !featureFlags.includes('membership_revenue_schedule')
  )

  // If the feature flag is enabled, manual payments need to be recorded
  // against the charge
  canRecordManualPayment =
    canRecordManualPayment && !featureFlags.includes('membership_revenue_schedule')

  const { push } = useHistory()

  const goToOrder = () => {
    push('/orders/' + membership.basket_id + '/payments')
  }

  return (
    <Dropdown threeDots>
      {isLead && (
        <DropdownItem
          label="Edit"
          disabled={
            membership.status === 'inactive' ||
            membership.status === 'expired' ||
            !userCanManageClients
          }
          disabledReason={
            !userCanManageClients ? t('tooltip.missing_permission.clients_manage') : undefined
          }
          subtitle={
            membership.status === 'inactive' || membership.status === 'expired'
              ? "This membership can't be edited"
              : ''
          }
          onClick={() => onEdit(membership.id)}
          icon={PencilIcon}
        />
      )}

      {
        <>
          <DropdownItem
            label="Manage members"
            onClick={() => onManageMembers(membership.id)}
            icon={UsersIcon}
            disabled={!userCanManageClients}
            disabledReason={t('tooltip.missing_permission.clients_manage')}
          />
          {!isLead && leadMember && (
            <DropdownItem
              label="Go to lead member"
              icon={ExternalLinkIcon}
              inAppLink
              href={`/clients/${leadMember.customer_id}`}
            />
          )}
        </>
      }

      {canRetryPayment && isLead && (
        <DropdownItem
          label="Retry payment"
          onClick={() => onRetryPayment(membership.id)}
          icon={RefreshIcon}
          disabled={!userCanManageClients}
          disabledReason={t('tooltip.missing_permission.clients_manage')}
        />
      )}

      {membership.status === 'needs_attention' &&
        membership.attention_reason === 'setup_unpaid' &&
        isLead && (
          <DropdownItem
            onClick={() => goToOrder()}
            label="Pay setup fee"
            icon={CashIcon}
            disabled={!userCanManageClients}
            disabledReason={t('tooltip.missing_permission.clients_manage')}
          />
        )}

      {canRecordManualPayment &&
        isLead &&
        (!featureFlags.includes('membership_revenue_schedule') ||
          membership.next_charge !== null) && (
          <DropdownItem
            onClick={() => onAddPayment(membership.id)}
            label="Record payment"
            icon={CashIcon}
            disabled={!userCanManageClients}
            disabledReason={t('tooltip.missing_permission.clients_manage')}
          />
        )}

      {canRequestMandate && (
        <>
          <DropdownDivider />
          <DropdownItem
            label="Request payment details"
            onClick={() => onRequestMandate(membership.id)}
            icon={MailIcon}
            disabled={!userCanManageClients}
            disabledReason={t('tooltip.missing_permission.clients_manage')}
          />
          {membership.payment_details_url && (
            <ClipboardDropdownItem
              label="Copy payment link"
              copyableValue={membership.payment_details_url}
              disabled={!userCanManageClients}
              disabledReason={t('tooltip.missing_permission.clients_manage')}
            />
          )}
          <DropdownDivider />
        </>
      )}

      <DropdownItem label="See audit log" onClick={onOpenAuditLog} icon={BookOpenIcon} />

      {isInternalUser && <ClipboardDropdownItem label="Copy ID" copyableValue={membership.id} />}

      {isLead && <DropdownDivider />}

      {membership.status !== 'inactive' && membership.status !== 'expired' && isLead && (
        <DropdownItem
          label="Cancel"
          onClick={() => onCancel(membership.id)}
          icon={XIcon}
          disabled={!userCanManageClients}
          disabledReason={t('tooltip.missing_permission.clients_manage')}
        />
      )}

      {(membership.status === 'inactive' || membership.status === 'expired') && isLead && (
        <DropdownItem
          label="Archive"
          onClick={() => onArchive(membership.id)}
          icon={ArchiveIcon}
          disabled={!userCanManageClients}
          disabledReason={t('tooltip.missing_permission.clients_manage')}
        />
      )}
    </Dropdown>
  )
}
